import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import usePath from '../../../components/Hooks/usePath';
import LangEnum from '../../../lang/LangEnum';
import PATHS from '../../../routes/routes.const';
import {
  useForceReverseSyncOrderMutation,
  useForceSyncOrderMutation,
} from '../../../services/modules/order/order.mutations';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import snackbarStore from '../../../stores/Snackbar.store';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { isOfflineStatus } from '../../offline/domain/offlineOrder/offlineOrder.types';
import { useTranslation } from '../../translation/TranslationContext';
import { ActionEnum } from '../actions/types/orderActions.types';
import { OrderActionProps } from '../actions/types/orderActionsContext.types';
import useUpdateOrder from '../forceUpdate/hooks/useUpdateOrder';
import { shouldForceUpdateOrder } from '../forceUpdate/utils/forceUpdateOrder.utils';
import useChangeOrderStatus from '../hooks/useChangeOrderStatus';
import { useGetOrderTotals } from '../hooks/useOrderTotals/order-totals.queries';
import { StatusType } from '../order.types';
import { authorizeOrderPayment } from '../payment/orderPayment.utils';
import orderActionsRepository from '../repositories/orderActions.repository';
import { confirmOrderSplitModal } from '../utils/confirmOrderSplitModal.utils';
import { getOrderActivitiesTabPath } from '../utils/orderActivitiesTab.utils';
import {
  OrderActionsContextProps,
  OrderActionsContextProviderProps,
} from './orderActions.context.types';
import { ModalState, initialModalState } from './orderApplication.state';

const OrderActionsContext = createContext<OrderActionsContextProps>({
  hasForceUpdatedOrder: false,
  isOrderOutdated: false,
  isSubmittingAction: false,
  modalState: initialModalState,
  onClickOrderAction: () => Promise.resolve(),
  onCloseModal: () => undefined,
  orderRaw: undefined,
  reloadCurrent: () => undefined,
  setReload: () => undefined,
});

export const OrderActionsContextProvider = ({
  children,
  isOfflineMode: _isOfflineMode,
  ignoreDefaultChangeOrderOnSuccessHandling,
}: OrderActionsContextProviderProps) => {
  const history = useHistory();
  const { handleMessage } = snackbarStore;
  const location = useLocation<{ filters: any }>();
  const { translate } = useTranslation();
  const { team } = useApplicationContext();
  const { validateOrUpdateOrder } = useUpdateOrder();
  const [handleRemoveOrder] = useOrderBookStore(useShallow((state) => [state.handleRemoveOrder]));
  const { refetch: refetchOrderTabs } = useGetOrderTotals();
  const { forceSyncOrder } = useForceSyncOrderMutation();
  const { forceReverseSyncOrder } = useForceReverseSyncOrderMutation();

  const [modalState, setModalState] = useState<ModalState>(initialModalState);
  const [hasForceUpdatedOrder, setHasForceUpdatedOrder] = useState(false);
  const [isOrderOutdated, setIsOrderOutdated] = useState(false);
  const [orderRaw, setOrderRaw] = useState<OrderActionProps | undefined>(undefined);
  const reload = useRef<(newStatus?: StatusType) => void>();

  const { isPath } = usePath();

  const reloadCurrent = (newStatus?: StatusType) => {
    if (reload.current) {
      reload.current(newStatus);
    }
  };

  const { onChangeStatus, isSubmittingAction } = useChangeOrderStatus({
    onSuccess: (newStatus?: StatusType) => {
      reloadCurrent(newStatus);
      !ignoreDefaultChangeOrderOnSuccessHandling && refetchOrderTabs();
    },
  });

  const validateOrUpdate = useCallback(
    async (orderId: string, orderStatus: StatusType) => {
      const forceUpdate = shouldForceUpdateOrder(orderStatus);
      const { isOrderOutdated, hasUpdatedOrder } = await validateOrUpdateOrder(
        orderId,
        forceUpdate,
      );
      if (isOrderOutdated) {
        setIsOrderOutdated(true);
        setModalState({
          show: true,
          modal: ActionEnum.OPEN_OUTDATED_ORDER_MODAL,
        });
      } else if (hasUpdatedOrder) {
        setModalState({
          show: true,
          modal: ActionEnum.OPEN_ORDER_WAS_UPDATED_MODAL,
        });
      }
    },
    [validateOrUpdateOrder],
  );

  const onFinishOrder = useCallback(
    async (orderId: string, action: ActionEnum, orderStatus: StatusType) => {
      const forceUpdate = shouldForceUpdateOrder(orderStatus);
      const { isOrderOutdated, hasUpdatedOrder } = await validateOrUpdateOrder(
        orderId,
        forceUpdate,
      );
      if (isOrderOutdated) {
        setIsOrderOutdated(true);
      } else if (hasUpdatedOrder) {
        setHasForceUpdatedOrder(true);
      }
      setModalState({ show: true, modal: action });
      return;
    },
    [validateOrUpdateOrder],
  );

  const onAcceptSuggestionOrder = async (
    orderId: string,
    action: ActionEnum,
    orderStatus: StatusType,
  ) => {
    const forceUpdate = shouldForceUpdateOrder(orderStatus);
    const { isOrderOutdated, hasUpdatedOrder } = await validateOrUpdateOrder(orderId, forceUpdate);

    if (isOrderOutdated) {
      setIsOrderOutdated(true);
    } else if (hasUpdatedOrder) {
      setHasForceUpdatedOrder(true);
    }
    setModalState({ show: true, modal: action });
  };

  const onClickOrderAction = useCallback(
    async (action: ActionEnum, order: OrderActionProps): Promise<any> => {
      const currentOrder = { orderId: undefined, ...order };
      setOrderRaw(currentOrder);

      const { orderId, orderCode, orderStatus } = currentOrder;
      if (!orderId || !orderCode || !orderStatus) {
        throw Error('undefined order on calling order actions');
      }

      const repository = orderActionsRepository({
        orderId,
        translate,
        brandId: currentOrder?.brandId,
      });

      // TODO: todas essas ações não precisam de nada do objeto order
      switch (action) {
        case ActionEnum.OPEN_SIGN_ORDER_MODAL:
        case ActionEnum.CONFIRM_BACK_TO_DRAFT:
        case ActionEnum.COPY_AS_DRAFT:
        case ActionEnum.COPY_AS_SUGGESTED:
        case ActionEnum.EXPORT_ORDER:
        case ActionEnum.OPEN_CONTRACT_MODAL:
        case ActionEnum.VIEW_CONTRACTS:
        case ActionEnum.OPEN_ORDER_HISTORIC_MODAL:
        case ActionEnum.OPEN_UPDATE_ORDER_MODAL:
        case ActionEnum.OPEN_OUTDATED_ORDER_MODAL:
        case ActionEnum.OPEN_ORDER_PAYMENT_MODAL:
        case ActionEnum.OPEN_ORDER_PAYMENT_HISTORY_MODAL:
        case ActionEnum.OPEN_ORDER_REFUND_PAYMENT_MODAL:
        case ActionEnum.OPEN_CANCEL_PAYMENT_LINK_MODAL:
        case ActionEnum.SHARE_ORDER:
        case ActionEnum.VERUP_EXPORT_CSV:
        case ActionEnum.MOVE_TO_REJECTED:
        case ActionEnum.SYNC_OFFLINE_ORDER:
        case ActionEnum.MOVE_TO_CANCELED:
          setModalState({ show: true, modal: action });
          return;

        case ActionEnum.FINISH_TO_IN_REVISION:
        case ActionEnum.FINISH_TO_ON_APPROVAL:
        case ActionEnum.FINISH_TO_APPROVED:
          if (isOfflineStatus(orderStatus)) {
            return;
          }
          return onFinishOrder(orderId, action, orderStatus);

        case ActionEnum.SYNC:
          await repository.sync();
          reloadCurrent();

          setTimeout(() => {
            reloadCurrent();
          }, 45000);
          return;

        case ActionEnum.VIEW_ORDER_ACTIVITIES:
          history.push(getOrderActivitiesTabPath({ orderId }));
          return;

        case ActionEnum.CHECK_ORDER_INTEGRATION:
          history.push(getOrderActivitiesTabPath({ orderId, domain: ['integration'] }));
          return;

        case ActionEnum.FORCE_SYNC_ORDER:
          forceSyncOrder({ orderId });
          return;

        case ActionEnum.FORCE_REVERSE_SYNC_ORDER:
          forceReverseSyncOrder({ orderId });
          return;

        case ActionEnum.DOWNLOAD_MEDIAS:
          return repository.downloadMedias(
            orderCode,
            currentOrder?.brandCommercialName ||
              translate(LangEnum.ORDER_CONTEXT_NO_COMMERCIAL_NAME),
          );

        case ActionEnum.VALIDATE_OR_UPDATE_ORDER:
          if (isOfflineStatus(orderStatus)) {
            return;
          }
          return validateOrUpdate(orderId, orderStatus);

        case ActionEnum.SPLIT_ORDER:
          confirmOrderSplitModal({
            orderCode: currentOrder.orderCode,
            onConfirm: () => {
              repository.splitOrder().then((success) => {
                if (success) {
                  if (!isPath(PATHS.main.orders.path)) {
                    history.replace({ pathname: '/orders', search: location.state?.filters });
                  }
                  handleRemoveOrder(orderId, team?.id);
                  reloadCurrent();
                  refetchOrderTabs();
                }
              });
            },
          });
          return;

        case ActionEnum.AUTHORIZE_ORDER_PAYMENT: {
          if (!order.orderSplitted) {
            return handleMessage({
              type: 'info',
              message: translate(LangEnum.ORDER_PAYMENT_SPLIT_ORDER_BEFORE_AUTHORIZE_PAYMENT),
            });
          }
          await authorizeOrderPayment(orderId, translate);
          reloadCurrent();
          return;
        }

        case ActionEnum.ACCEPT_SUGGESTION: {
          if (isOfflineStatus(orderStatus)) {
            return;
          }

          return onAcceptSuggestionOrder(orderId, action, orderStatus);
        }

        case ActionEnum.ARCHIVE_ORDER:
        case ActionEnum.BACK_TO_APPROVED:
        case ActionEnum.BACK_TO_DRAFT:
        case ActionEnum.BACK_TO_IN_REVISION:
        case ActionEnum.BACK_TO_ON_APPROVAL:
        case ActionEnum.MOVE_TO_SENT:
        case ActionEnum.UNARCHIVE_ORDER:
          return onChangeStatus({
            action,
            orderId,
            brandId: currentOrder?.brandId,
          });

        default:
          break;
      }
    },
    [
      team,
      history,
      translate,
      handleMessage,
      onFinishOrder,
      onChangeStatus,
      validateOrUpdate,
      handleRemoveOrder,
      forceSyncOrder,
      forceReverseSyncOrder,
      location.state?.filters,
    ],
  );

  const onCloseModal = () => {
    setModalState(initialModalState);
    setHasForceUpdatedOrder(false);
    setIsOrderOutdated(false);
  };

  const setReload = useCallback((reloadFn: (newStatus?: StatusType) => void) => {
    reload.current = reloadFn;
  }, []);

  const value: OrderActionsContextProps = {
    hasForceUpdatedOrder,
    isOrderOutdated,
    isSubmittingAction,
    modalState,
    onClickOrderAction,
    onCloseModal,
    orderRaw,
    reloadCurrent,
    setReload,
  };

  return <OrderActionsContext.Provider value={value}>{children}</OrderActionsContext.Provider>;
};

export const useOrderActionsContext = () =>
  useContext<OrderActionsContextProps>(OrderActionsContext);
